import { useEffect } from "react";
import SEOHead from "@/components/feature/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { brandInfo, reviews } from "@/mocks/brand";

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const aboutSchema = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "JM网页版官网入口",
      url: "https://jm-official.com",
      description: brandInfo.description,
      foundingDate: "2020",
      contactPoint: {
        "@type": "ContactPoint",
        email: brandInfo.contact.email,
        telephone: brandInfo.contact.phone,
        contactType: "customer service",
        availableLanguage: "Chinese",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "关于我们 - JM网页版官网入口",
      description: brandInfo.description,
      url: "https://jm-official.com/about",
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们"
        description={brandInfo.description}
        keywords="JM网页版官网入口,关于JM,JM官网入口,联系方式,jm网页版地址"
        canonicalUrl="/about"
        schemaData={aboutSchema}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: "关于我们" },
          ]}
        />

        {/* Brand Intro */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
                  关于我们
                </span>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4">
                  {brandInfo.fullName}
                </h1>
                <p className="text-lg text-accent-600 font-medium mb-5">
                  {brandInfo.slogan}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  {brandInfo.description}
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-background-50 rounded-lg border border-background-200">
                    <div className="text-xl font-bold text-accent-600">
                      {brandInfo.founded}
                    </div>
                    <div className="text-xs text-foreground-500">成立时间</div>
                  </div>
                  <div className="text-center p-3 bg-background-50 rounded-lg border border-background-200">
                    <div className="text-xl font-bold text-accent-600">
                      500万+
                    </div>
                    <div className="text-xs text-foreground-500">月活用户</div>
                  </div>
                  <div className="text-center p-3 bg-background-50 rounded-lg border border-background-200">
                    <div className="text-xl font-bold text-accent-600">
                      99.9%
                    </div>
                    <div className="text-xs text-foreground-500">服务可用率</div>
                  </div>
                </div>
              </div>
              <div className="flex-1 w-full">
                <div className="h-72 md:h-80 w-full rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20collaborative%20workspace%20with%20team%20working%20on%20laptops%2C%20warm%20ambient%20lighting%2C%20minimalist%20Scandinavian%20design%2C%20clean%20aesthetic%2C%20plants%20decor%2C%20professional%20creative%20environment%2C%20soft%20golden%20hour%20light&width=800&height=500&seq=about-brand&orientation=landscape"
                    alt="JM网页版官网入口 - 品牌形象"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg mb-4">
                  <i className="ri-focus-3-line text-lg text-accent-600"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">
                  我们的使命
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {brandInfo.mission}
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg mb-4">
                  <i className="ri-eye-line text-lg text-secondary-600"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3">
                  我们的愿景
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {brandInfo.vision}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
                核心价值观
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                我们始终坚持的信念
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {brandInfo.values.map((v, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 rounded-xl p-6 border border-background-200/70 text-center"
                >
                  <div className="w-12 h-12 mx-auto flex items-center justify-center bg-accent-500 rounded-xl mb-4">
                    <span className="text-lg font-bold text-background-50">
                      {idx + 1}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">
                    {v.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {v.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
                发展历程
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                我们的成长足迹
              </h2>
            </div>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
              <div className="space-y-8">
                {brandInfo.milestones.map((m, idx) => (
                  <div
                    key={idx}
                    className={`relative flex items-start gap-6 ${
                      idx % 2 === 0
                        ? "md:flex-row"
                        : "md:flex-row-reverse"
                    }`}
                  >
                    <div className="absolute left-2.5 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 md:-translate-x-1.5 mt-1.5 z-10"></div>
                    <div
                      className={`ml-10 md:ml-0 md:w-1/2 ${
                        idx % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12"
                      }`}
                    >
                      <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 inline-block">
                        <span className="text-sm font-bold text-accent-600">
                          {m.year}
                        </span>
                        <p className="text-sm text-foreground-700 mt-1">
                          {m.event}
                        </p>
                      </div>
                    </div>
                    {idx % 2 !== 0 ? (
                      <div className="hidden md:block md:w-1/2"></div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
                用户口碑
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                用户对我们的评价
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {reviews.slice(0, 6).map((review) => (
                <div
                  key={review.id}
                  className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < review.rating
                            ? "ri-star-fill text-amber-400"
                            : "ri-star-line text-foreground-300"
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{review.content}"
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground-800">
                      {review.name}
                    </span>
                    <span className="text-xs text-foreground-400">
                      {review.date}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
                联系我们
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                获取帮助与支持
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="bg-background-100 rounded-xl p-6 text-center border border-background-200/70">
                <div className="w-12 h-12 mx-auto flex items-center justify-center bg-accent-100 rounded-xl mb-4">
                  <i className="ri-mail-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-2">
                  电子邮箱
                </h3>
                <p className="text-sm text-foreground-600 break-all">
                  {brandInfo.contact.email}
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center border border-background-200/70">
                <div className="w-12 h-12 mx-auto flex items-center justify-center bg-accent-100 rounded-xl mb-4">
                  <i className="ri-phone-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-2">
                  客服电话
                </h3>
                <p className="text-sm text-foreground-600">
                  {brandInfo.contact.phone}
                </p>
              </div>
              <div className="bg-background-100 rounded-xl p-6 text-center border border-background-200/70">
                <div className="w-12 h-12 mx-auto flex items-center justify-center bg-accent-100 rounded-xl mb-4">
                  <i className="ri-time-line text-xl text-accent-600"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-2">
                  服务时间
                </h3>
                <p className="text-sm text-foreground-600">
                  {brandInfo.contact.workingHours}
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}